import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import BusinessOutlined from '@material-ui/icons/BusinessOutlined';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import VpnKeyOutlined from '@material-ui/icons/VpnKeyOutlined';
import { useNotify, useTranslate } from 'react-admin';
import {
    createCompany,
    isValidSiret,
    linkPrestataireAffiliation,
    lookupPrestataireBySiret,
    normalizeSiret,
} from './api';
import Notice from './Notice';
import { useIsMobile } from '../../hooks/useIsMobile';
import TypesAndDepartmentsFields from './TypesAndDepartmentsFields';
import { AnnuaireCompany, fetchCompanyFromAnnuaire } from './annuaireEntreprises';
import { formatCompanyAddress } from './ignGeocode';
import { categoryTypesFromScopes, PrestataireScope, PrestataireSiretLookup, scopesAreValid } from './types';

interface AddPrestataireDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const emptyCreate = {
    name: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
    adminLastName: '',
    street: '',
    additionalAddress: '',
    postalCode: '',
    city: '',
    country: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP_SIRET = 0;
const STEP_COMPANY = 1;
const STEP_ZONE = 2;

/** '12345678900011' -> '123 456 789 00011' while typing; non-digits are dropped. */
export const formatSiretInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9), digits.slice(9)].filter(Boolean).join(' ');
};

const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%*?';

const generatePassword = (length = 14): string => {
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    return Array.from(values, (value) => PASSWORD_ALPHABET[value % PASSWORD_ALPHABET.length]).join('');
};

/** An existing company can be linked only if it is an eligible prestataire not yet affiliated to this fabricant. */
const canLinkExisting = (lookup: PrestataireSiretLookup) =>
    lookup.found && lookup.eligible !== false && !lookup.alreadyAffiliated;

const AddPrestataireDialog: React.FC<AddPrestataireDialogProps> = ({ open, onClose, onSuccess }) => {
    const translate = useTranslate();
    const isMobile = useIsMobile();
    const notify = useNotify();

    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [checkingSiret, setCheckingSiret] = useState(false);
    const [siret, setSiret] = useState('');
    const [lookup, setLookup] = useState<PrestataireSiretLookup | null>(null);
    const [scopes, setScopes] = useState<PrestataireScope[]>([]);
    const [createForm, setCreateForm] = useState(emptyCreate);
    const [showErrors, setShowErrors] = useState(false);
    /** Directory data the company form was prefilled with, keyed by SIRET so going back does not wipe user edits. */
    const [annuaire, setAnnuaire] = useState<{ siret: string; company: AnnuaireCompany | null } | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    const steps = useMemo(
        () => [
            translate('app.prestataires.wizard.steps.siret'),
            translate('app.prestataires.wizard.steps.company'),
            translate('app.prestataires.wizard.steps.zone'),
        ],
        [translate],
    );

    const reset = () => {
        setActiveStep(0);
        setSubmitting(false);
        setCheckingSiret(false);
        setSiret('');
        setLookup(null);
        setScopes([]);
        setCreateForm(emptyCreate);
        setAnnuaire(null);
        setShowErrors(false);
        setShowPassword(false);
    };

    const handleClose = () => {
        if (submitting) {
            return;
        }
        reset();
        onClose();
    };

    const busy = submitting || checkingSiret;
    /** Something was typed: a click outside the dialog must not throw it away. */
    const dirty = Boolean(siret.trim()) || activeStep > STEP_SIRET;
    const mode: 'link' | 'create' = lookup?.found ? 'link' : 'create';
    const siretValid = isValidSiret(siret);
    const typesValid = scopes.length > 0;
    const departmentsValid = scopesAreValid(scopes);
    const createValid =
        createForm.name.trim() &&
        EMAIL_PATTERN.test(createForm.adminEmail.trim()) &&
        createForm.adminPassword.trim();

    const initialAddress =
        mode === 'link'
            ? formatCompanyAddress(
                  lookup?.company?.street || '',
                  lookup?.company?.postalCode || '',
                  lookup?.company?.city || '',
              )
            : formatCompanyAddress(createForm.street, createForm.postalCode, createForm.city);

    const handleSiretChange = (value: string) => {
        setSiret(formatSiretInput(value));
        // A previous lookup no longer applies once the SIRET changes.
        setLookup(null);
        setShowErrors(false);
    };

    const handleCheckSiret = async () => {
        setShowErrors(true);
        if (!siretValid) {
            return;
        }

        setCheckingSiret(true);
        try {
            const result = await lookupPrestataireBySiret(siret);
            if (!result.found && annuaire?.siret !== normalizeSiret(siret)) {
                const company = await fetchCompanyFromAnnuaire(siret);
                setAnnuaire({ siret: normalizeSiret(siret), company });
                setCreateForm((prev) => ({
                    ...prev,
                    name: company?.name || '',
                    street: company?.street || '',
                    additionalAddress: company?.additionalAddress || '',
                    postalCode: company?.postalCode || '',
                    city: company?.city || '',
                    country: company?.country || '',
                }));
            }
            setLookup(result);
            setShowErrors(false);
            if (!result.found || canLinkExisting(result)) {
                setActiveStep(STEP_COMPANY);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : translate('app.prestataires.messages.error');
            notify(message, 'error');
        } finally {
            setCheckingSiret(false);
        }
    };

    const handleBack = () => {
        setShowErrors(false);
        setActiveStep((step) => Math.max(STEP_SIRET, step - 1));
    };

    /** Each step validates only its own fields, so errors always show where the user is. */
    const handleCompanyNext = () => {
        if (mode === 'create' && !createValid) {
            setShowErrors(true);
            return;
        }
        setShowErrors(false);
        setActiveStep(STEP_ZONE);
    };

    const handleSubmit = async () => {
        setShowErrors(true);
        if (!lookup || !typesValid || !departmentsValid) {
            return;
        }
        if (mode === 'create' && !createValid) {
            return;
        }

        setSubmitting(true);
        try {
            if (mode === 'link') {
                await linkPrestataireAffiliation({ siret: normalizeSiret(siret), scopes });
            } else {
                const optional = (value: string) => (value.trim() ? value.trim() : undefined);
                await createCompany({
                    name: createForm.name.trim(),
                    invoiceId: `anc-hub:${normalizeSiret(siret)}`,
                    // The admin signs in with their email: no separate username.
                    adminUsername: createForm.adminEmail.trim(),
                    adminEmail: createForm.adminEmail.trim(),
                    adminPassword: createForm.adminPassword,
                    adminName: optional(createForm.adminName),
                    adminLastName: optional(createForm.adminLastName),
                    street: optional(createForm.street),
                    additionalAddress: optional(createForm.additionalAddress),
                    postalCode: optional(createForm.postalCode),
                    city: optional(createForm.city),
                    country: optional(createForm.country),
                    categoryTypes: categoryTypesFromScopes(scopes),
                    provider: { scopes },
                });
            }

            notify(translate('app.prestataires.messages.createSuccess'), 'success');
            reset();
            onSuccess();
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : translate('app.prestataires.messages.error');
            notify(message, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const updateCreateField = (field: keyof typeof emptyCreate, value: string) => {
        setCreateForm((prev) => ({ ...prev, [field]: value }));
    };

    const createTextField = (
        field: keyof typeof emptyCreate,
        options: {
            required?: boolean;
            type?: string;
            invalid?: boolean;
            readOnly?: boolean;
            errorText?: string;
            helperText?: string;
            autoFocus?: boolean;
            autoComplete?: string;
            InputProps?: Record<string, unknown>;
        } = {},
    ) => {
        const hasError = showErrors && Boolean(options.invalid);
        return (
            <TextField
                fullWidth
                margin='dense'
                variant='outlined'
                required={options.required}
                type={options.type}
                autoFocus={options.autoFocus}
                autoComplete={options.autoComplete}
                label={translate(`app.prestataires.fields.${field === 'name' ? 'companyName' : field}`)}
                value={createForm[field]}
                onChange={(event) => updateCreateField(field, event.target.value)}
                InputProps={{ ...(options.readOnly ? { readOnly: true } : {}), ...(options.InputProps || {}) }}
                error={hasError}
                helperText={
                    hasError
                        ? options.errorText || translate('app.prestataires.validation.required')
                        : options.helperText
                }
                disabled={submitting}
            />
        );
    };

    const companyName = mode === 'link' ? lookup?.company?.name : createForm.name;
    const stepCaption = (index: number): string | undefined => {
        if (index >= activeStep) {
            return undefined;
        }
        if (index === STEP_SIRET) {
            return formatSiretInput(siret);
        }
        if (index === STEP_COMPANY) {
            return companyName || undefined;
        }
        return undefined;
    };

    const renderSiretStep = () => (
        <Box maxWidth={520} marginX='auto' paddingY={2}>
            <Typography variant='body2' color='textSecondary'>
                {translate('app.prestataires.wizard.siretHelp')}
            </Typography>
            <TextField
                fullWidth
                margin='normal'
                variant='outlined'
                required
                autoFocus
                label={translate('app.prestataires.fields.siret')}
                value={siret}
                placeholder='123 456 789 00012'
                onChange={(event) => handleSiretChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        handleCheckSiret();
                    }
                }}
                inputProps={{ inputMode: 'numeric', style: { fontSize: 20, letterSpacing: 1 } }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <BusinessOutlined color='action' />
                        </InputAdornment>
                    ),
                    endAdornment: checkingSiret ? <CircularProgress size={20} /> : null,
                }}
                error={showErrors && !siretValid}
                helperText={
                    showErrors && !siretValid
                        ? translate('app.prestataires.validation.siretInvalid')
                        : translate('app.prestataires.wizard.siretDigits', {
                              count: normalizeSiret(siret).length,
                          })
                }
                disabled={busy}
            />

            {lookup?.found && lookup.alreadyAffiliated && (
                <Notice severity='warning'>
                    {translate('app.prestataires.wizard.alreadyAffiliated', {
                        name: lookup.company?.name || '',
                    })}
                </Notice>
            )}
            {lookup?.found && !lookup.alreadyAffiliated && lookup.eligible === false && (
                <Notice severity='error'>
                    {translate('app.prestataires.wizard.notEligible', {
                        name: lookup.company?.name || '',
                    })}
                </Notice>
            )}
        </Box>
    );

    const renderLinkCompany = () => (
        <Box maxWidth={620} marginX='auto' paddingY={1}>
            <Notice severity='success' title={translate('app.prestataires.wizard.found')}>
                {translate('app.prestataires.wizard.foundHelp')}
            </Notice>
            <Paper variant='outlined' style={{ padding: 20, marginTop: 12, display: 'flex', gap: 16 }}>
                <BusinessOutlined color='primary' style={{ fontSize: 40 }} />
                <Box>
                    <Typography variant='h6'>{lookup?.company?.name}</Typography>
                    <Typography variant='body2' color='textSecondary'>
                        {`${translate('app.prestataires.fields.siret')} ${formatSiretInput(siret)}`}
                    </Typography>
                    <Typography variant='body2' color='textSecondary'>
                        {formatCompanyAddress(
                            lookup?.company?.street || '',
                            lookup?.company?.postalCode || '',
                            lookup?.company?.city || '',
                        )}
                    </Typography>
                    {lookup?.company?.email && (
                        <Typography variant='body2' color='textSecondary'>
                            {lookup.company.email}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
    );

    const renderCreateCompany = () => (
        <Box>
            <Notice severity='info' title={translate('app.prestataires.wizard.notFound', { siret: formatSiretInput(siret) })}>
                {annuaire?.company
                    ? translate('app.prestataires.wizard.annuairePrefilled')
                    : translate('app.prestataires.wizard.createHelp')}
            </Notice>
            {annuaire?.company?.closed && (
                <Notice severity='warning'>{translate('app.prestataires.wizard.annuaireClosed')}</Notice>
            )}

            <Typography variant='subtitle1' style={{ marginTop: 16, fontWeight: 600 }}>
                {translate('app.prestataires.wizard.companyBlock')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                    {createTextField('name', {
                        required: true,
                        invalid: !createForm.name.trim(),
                        // Official name from the Annuaire des Entreprises: not editable.
                        readOnly: Boolean(annuaire?.company?.name),
                        autoFocus: !annuaire?.company?.name,
                    })}
                </Grid>
                <Grid item xs={12} sm={4}>
                    {/* Checked in step 1; change it with "Retour" so it is looked up again. */}
                    <TextField
                        fullWidth
                        margin='dense'
                        variant='outlined'
                        required
                        label={translate('app.prestataires.fields.siret')}
                        value={normalizeSiret(siret)}
                        InputProps={{ readOnly: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    {createTextField('street')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    {createTextField('additionalAddress')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    {createTextField('postalCode')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    {createTextField('city')}
                </Grid>
                <Grid item xs={12} sm={4}>
                    {createTextField('country')}
                </Grid>
            </Grid>

            <Typography variant='subtitle1' style={{ marginTop: 24, fontWeight: 600 }}>
                {translate('app.prestataires.wizard.adminBlock')}
            </Typography>
            <Typography variant='body2' color='textSecondary'>
                {translate('app.prestataires.wizard.adminHelp')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    {createTextField('adminName', { autoFocus: Boolean(annuaire?.company?.name) })}
                </Grid>
                <Grid item xs={12} sm={6}>
                    {createTextField('adminLastName')}
                </Grid>
                <Grid item xs={12} sm={6}>
                    {createTextField('adminEmail', {
                        required: true,
                        type: 'email',
                        autoComplete: 'off',
                        invalid: !EMAIL_PATTERN.test(createForm.adminEmail.trim()),
                        errorText: translate('app.prestataires.validation.emailInvalid'),
                        helperText: translate('app.prestataires.wizard.adminEmailIsLogin'),
                    })}
                </Grid>
                <Grid item xs={12} sm={6}>
                    {createTextField('adminPassword', {
                        required: true,
                        type: showPassword ? 'text' : 'password',
                        autoComplete: 'new-password',
                        invalid: !createForm.adminPassword.trim(),
                        InputProps: {
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <Tooltip title={translate('app.prestataires.wizard.generatePassword')}>
                                        <IconButton
                                            size='small'
                                            aria-label={translate('app.prestataires.wizard.generatePassword')}
                                            onClick={() => {
                                                updateCreateField('adminPassword', generatePassword());
                                                setShowPassword(true);
                                            }}
                                        >
                                            <VpnKeyOutlined fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton
                                        size='small'
                                        aria-label={translate(
                                            showPassword
                                                ? 'app.prestataires.wizard.hidePassword'
                                                : 'app.prestataires.wizard.showPassword',
                                        )}
                                        onClick={() => setShowPassword((value) => !value)}
                                    >
                                        {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    })}
                </Grid>
            </Grid>
        </Box>
    );

    const renderZoneStep = () => (
        <Box>
            <Typography variant='body2' color='textSecondary'>
                {translate('app.prestataires.wizard.zoneHelp', { name: companyName || '' })}
            </Typography>
            <TypesAndDepartmentsFields
                scopes={scopes}
                onScopesChange={setScopes}
                initialAddress={initialAddress}
                disabled={submitting}
                errorTypes={showErrors && !typesValid}
                errorDepartments={showErrors && !departmentsValid}
            />
        </Box>
    );

    const primaryAction = () => {
        if (activeStep === STEP_SIRET) {
            return { label: 'app.prestataires.actions.next', onClick: handleCheckSiret, loading: checkingSiret };
        }
        if (activeStep === STEP_COMPANY) {
            return { label: 'app.prestataires.actions.next', onClick: handleCompanyNext, loading: false };
        }
        return {
            label: mode === 'link' ? 'app.prestataires.actions.link' : 'app.prestataires.actions.createAndLink',
            onClick: handleSubmit,
            loading: submitting,
        };
    };
    const primary = primaryAction();

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            fullScreen={isMobile}
            maxWidth={activeStep === STEP_ZONE ? 'lg' : 'md'}
            disableBackdropClick={busy || dirty}
        >
            <DialogTitle>{translate('app.prestataires.wizard.title')}</DialogTitle>
            <DialogContent style={{ minHeight: 320 }}>
                <Stepper activeStep={activeStep} alternativeLabel style={{ padding: '8px 0 24px' }}>
                    {steps.map((label, index) => {
                        const caption = stepCaption(index);
                        return (
                            <Step key={label}>
                                <StepLabel
                                    optional={
                                        caption ? (
                                            <Typography variant='caption' color='textSecondary' noWrap component='div'>
                                                {caption}
                                            </Typography>
                                        ) : undefined
                                    }
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>

                {activeStep === STEP_SIRET && renderSiretStep()}
                {activeStep === STEP_COMPANY && lookup && (mode === 'link' ? renderLinkCompany() : renderCreateCompany())}
                {activeStep === STEP_ZONE && lookup && renderZoneStep()}
            </DialogContent>
            <DialogActions style={{ padding: isMobile ? '12px 16px' : '12px 24px' }}>
                <Button onClick={handleClose} disabled={submitting}>
                    {translate('app.prestataires.actions.cancel')}
                </Button>
                <Box flex={1} />
                {activeStep > STEP_SIRET && (
                    <Button onClick={handleBack} disabled={submitting}>
                        {translate('app.prestataires.actions.back')}
                    </Button>
                )}
                <Button color='primary' variant='contained' onClick={primary.onClick} disabled={busy}>
                    {primary.loading ? <CircularProgress size={20} color='inherit' /> : translate(primary.label)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddPrestataireDialog;
